import type { ElementRef } from '@angular/core';
import {
    ChangeDetectorRef,
    Directive,
    inject,
    Injector,
    Input,
    type OnDestroy,
    type OnInit,
    ViewChild,
} from '@angular/core';
import type { Container, Widget } from '@common/types/content.type';
import type { Classes, JssStyle, StyleSheet } from 'jss';
import { DomSanitizer } from '@angular/platform-browser';
import type { Observable } from 'rxjs';
import { shareReplay, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { BaseStylesService } from '@common/services/base-styles.service';
import { getGsapAnimation } from '@components/sp-animation-settings';
import { FmFeatureApiService } from '@sendpulse/file-manager';

@Directive()
export abstract class SpBaseViewComponent<
    T extends object,
    W extends Widget = Widget,
    C extends Container<W> = Container<W>,
>
    implements OnInit, OnDestroy
{
    @Input() public container$: Observable<C>;
    @Input() public widget$: Observable<W>;
    @Input() public isSpecific: boolean = false;

    @ViewChild('animatedElem') animatedElem: ElementRef<HTMLDivElement>;

    // @Input() public routeIds?;

    protected readonly stylesService: BaseStylesService = inject(BaseStylesService);
    protected readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    private readonly fmFeatureApiService = inject(FmFeatureApiService);

    public container: C;
    public widget: W;
    public classes: Classes;
    private sheet: StyleSheet;
    public cdnInfo$ = this.fmFeatureApiService.cdnInfo$.pipe(filter(Boolean), shareReplay(1));

    public isSafari: boolean;

    protected unsubscribe$ = new Subject<void>();
    protected abstract getStyles: (widget: W, config?: { isConstructor: boolean; isSpecific?: boolean }) => JssStyle;
    protected widgetCallback?(widget: W): any;
    protected withContainerIdCallback?: (containerId: string) => any;

    public ngOnInit(): void {
        this.container$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((container: C) => Boolean(container)),
                map((container: C) => {
                    const containerId = (container as any).parentId ? (container as any).parentId : container.id;
                    if (this.withContainerIdCallback) {
                        this.withContainerIdCallback(containerId);
                    }
                    return container;
                }),
                tap((container: C) => (this.container = container))
            )
            .subscribe();

        this.widget$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((widget: W) => Boolean(widget))
            )
            .subscribe((widget: W) => {
                const widgetData = widget.data as T;
                let animationCurr, animationPrev;

                if (widgetData && 'animation' in widgetData) {
                    animationCurr = widgetData.animation;
                }
                if (this.widgetData && 'animation' in this.widgetData) {
                    animationPrev = this.widgetData.animation;
                }

                const isAnimationChanged = !(JSON.stringify(animationCurr) === JSON.stringify(animationPrev));

                const ua = navigator.userAgent;
                this.isSafari = ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');

                this.widget = widget;
                this.sheet = this.stylesService.createWidgetStyle(
                    this.widget.id,
                    this.getStyles(this.widget, { isConstructor: true, isSpecific: this.isSpecific })
                );
                this.classes = this.sheet.classes;

                if (this.widgetCallback) {
                    this.widgetCallback(widget);
                }

                this.changeDetectorRef.detectChanges();

                if (this.animatedElem && animationCurr && animationCurr.isActive && isAnimationChanged) {
                    getGsapAnimation(animationCurr, this.animatedElem.nativeElement);
                }
            });
    }

    public get widgetData(): T {
        return this.widget?.data as T;
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        if (this.widget) this.stylesService.removeWidgetStyle(this.widget.id, this.sheet);
    }
}
